import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { BUCKET_NAMES, TEMP_NO_FITSCORE_POSITIONS } from "src/constants/globalConstants";
import PdfReportApi from "src/services/apis/pdfReport";
import { capitalizeTitle, getTraitsMap } from "src/utils/helperFunctions";
import FitScore from "../FitScore";
import ReportHeader from "../Header";
import BucketDetails from "./BucketDetails";
import PersonalityComponent from "../PersonalityComponent/PersonalityComponent";
import { ProctoringModal } from "./ProctoringModal";
import useCreatedBucketsFromReport from "./useCreatedBucketsFromReport";
import { ReportPdfContext } from "src/utils/contexts";
import { AI_PROCTORING } from "../constant";
import './v2.scss'
import TraitsGrid from "../TraitsGrid";
import OverallSummary from "./OverallSummary";
import CutoffTraitsTable from "./CutoffTraitsTable";
import BehaviouralAnalysis from "./BehaviouralAnalysis";
import CandidateApi from "src/services/apis/candidate";
import CandidateDetailsSection, { normalizeCandidateFormData } from "src/pages/Positions/CandidateFormData/CandidateDetailsSection";
import "src/pages/Positions/CandidateFormData/CandidateFormData.scss";

const candidateFormDataRequestInFlight = new Set();

export const FUNCTIONAL_BUCKETS = Object.values(BUCKET_NAMES).filter(
  (bucket) =>
    ![
      BUCKET_NAMES.COMMUNICATION,
      /** Personality Component Buckets */
      BUCKET_NAMES.PERSONALITY_DIMENSIONS,
      BUCKET_NAMES.IPIP_LONG,
      BUCKET_NAMES.IPIP_SHORT,
      /***/
      BUCKET_NAMES.COMPETENCIES,
      BUCKET_NAMES.APTITUDE_AND_REASONING
    ].includes(bucket)
);

const hasSnapshotCriticalReason = (snap) => {
  if (typeof snap?.criticalReason === "string") {
    return snap.criticalReason.trim().length > 0
  }
  return !!snap?.criticalReason
}

const getSnapshotTimeInSeconds = (snap, fallbackSeconds) => {
  const timestamp = Number(snap?.timestamp)
  if (!Number.isNaN(timestamp)) return timestamp

  const createdAt = new Date(snap?.createdAt).getTime()
  if (!Number.isNaN(createdAt)) return Math.floor(createdAt / 1000)

  return fallbackSeconds
}

const filterSkippedAiSnapshots = (snapshots, intervalInSeconds = 30) => {
  let intervalAnchor = null

  return snapshots.filter((snap, index) => {
    const snapshotTime = getSnapshotTimeInSeconds(snap, index * intervalInSeconds)
    if (intervalAnchor === null) intervalAnchor = snapshotTime

    if (hasSnapshotCriticalReason(snap)) {
      return true
    }

    if (index === 0 && !snap?.aiProctoringData) return false

    return (snapshotTime - intervalAnchor) % intervalInSeconds === 0
  })
}

export const V2 = ({ reportData, isNonDnaReport, logMixpanelEvent }) => {

  const { reportPdfView } = useContext(ReportPdfContext);

  const [activeBucketId, setActiveBucketId] = useState(null);
  const [activeTrait, setActiveTrait] = useState(null);
  const [allSnapshots, setAllSnapshots] = useState([])
  const [criticalSnapshots, setCriticalSnapshots] = useState([])
  const hoverDelayRef = useRef(null);
  const proctoredMediaModalRef = useRef()
  const [isProctoringModalVisible, setIsProctoringModalVisible] = useState(false)
  const [userVideoSessionsList, setUserVideoSessionsList] = useState([])
  const [linkConfig, setLinkConfig] = useState(null)
  const [positionConfig, setPositionConfig] = useState(null)
  const [referenceImageLink, setReferenceImageLink] = useState(null)
  const [candidateFormData, setCandidateFormData] = useState(null)
  const [candidateFormDataLoading, setCandidateFormDataLoading] = useState(false)
  const { linkId } = useParams();
  const { search } = useLocation();

  // Driven by the company's organizationConfig.miniReport flag, which the
  // position-config response nests under organization. Managers can append the
  // undocumented ?rv=full to see the complete report instead; it only applies
  // where the mini report is configured, since everyone else already gets full.
  const isMiniReportConfigured = positionConfig?.organization?.organizationConfig?.miniReport === true;
  const isFullReportOverride = useMemo(
    () => new URLSearchParams(search).get("rv")?.toLowerCase() === "full",
    [search]
  );
  const isMiniReportEnabled = isMiniReportConfigured && !isFullReportOverride;

  useEffect(() => {
    const scrolledWindow = document.querySelector(".content-wrapper");
    if (scrolledWindow) {
      scrolledWindow.scrollTo(0, 0);
    }
  }, [activeBucketId]);

  useEffect(() => {
    fetchLinkConfig()
    fetchPositionConfig()
    fetchCandidateFormData()
  }, [])

  const fetchCandidateFormData = async () => {
    if (candidateFormDataRequestInFlight.has(linkId)) return
    candidateFormDataRequestInFlight.add(linkId)
    try {
      setCandidateFormDataLoading(true)
      const response = await CandidateApi.getCandidateFormData(linkId)
      if (response.success) {
        setCandidateFormData(normalizeCandidateFormData(response.userData))
      }
    } catch (err) {
      console.error(err)
    } finally {
      setCandidateFormDataLoading(false)
      candidateFormDataRequestInFlight.delete(linkId)
    }
  }

  const fetchPositionConfig = async () => {
    try {
      const response = await PdfReportApi.getPositionConfig(linkId)
      if (response.success) {
        setPositionConfig(response.positionConfig)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchLinkConfig = async () => {
    try {
      const response = await PdfReportApi.getLinkPositionConfig(linkId)
      if (response.success) {
        setLinkConfig(response.linkPositionConfig)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchSessionSnapshots = async (sessionId, { includeReferenceImage = false } = {}) => {
    try {
      const response = await PdfReportApi.getSessionSnapshots(sessionId)
      if (response.success) {
        if (response.referenceImageLink) {
          setReferenceImageLink(response.referenceImageLink)
        }
        let updatedSnapshotsData
        if (positionConfig.screenProctoring || positionConfig.aiProctoring) {
          const showAllSnapshots = positionConfig.skipAiProcOnCutoffFail && reportData?.cutOffCleared === false
          const aiProctoringInterval = Number(positionConfig?.aiProctoringInterval) || 30
          updatedSnapshotsData = (showAllSnapshots ? filterSkippedAiSnapshots(response.data, aiProctoringInterval) : response.data.filter(snap => !!snap?.aiProctoringData))
            .map(snap => {
              const hasSkippedAiCriticalReason = !snap?.aiProctoringData && hasSnapshotCriticalReason(snap)
              const isAiCritical = [AI_PROCTORING.TYPES.CHEATING, AI_PROCTORING.TYPES.MAYBECHEATING].includes(snap?.aiProctoringData?.status)

              return {
                ...snap,
                critical: isAiCritical || hasSkippedAiCriticalReason,
                criticalType: hasSkippedAiCriticalReason ? AI_PROCTORING.TYPES.CHEATING : snap?.aiProctoringData?.status,
                criticalReason: snap?.aiProctoringData ? <>
                  <p className="text-white text-14 weight-500 mb-4">{capitalizeTitle(snap.aiProctoringData?.type)}</p>
                  <p className="text-white text-12 weight-400">{snap.aiProctoringData?.remark}</p>
                </> : hasSkippedAiCriticalReason ? (
                  <p className="text-white text-12 weight-400">{snap.criticalReason}</p>
                ) : null
              }
            })
        } else {
          updatedSnapshotsData = response.data.map(snap => ({
            ...snap,
            critical: snap?.criticalReason?.length > 0,
            criticalType: AI_PROCTORING.TYPES.CHEATING
          }))
        }
        if (includeReferenceImage) {
          return {
            snapshots: updatedSnapshotsData,
            referenceImageLink: response.referenceImageLink,
          }
        }
        return updatedSnapshotsData
      }
    } catch (err) {
      return includeReferenceImage ? { snapshots: [], referenceImageLink: null } : []
    }
  }

  const fetchSnapshotsForReportDownload = async () => {
    try {
      if (linkConfig?.proctoring || positionConfig?.proctoring) {
        return [allSnapshots.filter(snap => !snap.critical), criticalSnapshots]
      }
      let response = await Promise.allSettled(userVideoSessionsList.map(session => fetchSessionSnapshots(session.id, { includeReferenceImage: true })))
      response = response?.filter((result) => result.status === "fulfilled" && result.value?.snapshots)?.map((result) => result.value)
      let allVideoSnapshots = []
      let fetchedReferenceImageLink = referenceImageLink
      response.forEach(({ snapshots, referenceImageLink: sessionReferenceImageLink }) => {
        allVideoSnapshots.push(...snapshots)
        if (!fetchedReferenceImageLink && sessionReferenceImageLink) {
          fetchedReferenceImageLink = sessionReferenceImageLink
        }
      })
      return [allVideoSnapshots.filter(snap => !snap.critical), allVideoSnapshots.filter(snap => snap.critical), fetchedReferenceImageLink]
    } catch (err) {
      console.log(err)
      return [[], [], referenceImageLink]
    }
  }

  const {
    functionalBuckets,
    communicationBuckets,
    competenciesBuckets,
    aptitudeBuckets,
    personalityBuckets,
    personalityBucketsIPIPLong,
    personalityBucketsIPIPShort,
    checkOnlyPersonalityBucket,
    renderSpecialBuckets,
    otherBuckets,
    otherBucketsWithGridView
  } = useCreatedBucketsFromReport({
    reportData,
    reportPdfView,
    setActiveBucketId,
    // Mini-report companies get the compact bucket layout on the web report too:
    // per-trait scores inline, no "View Details" drill-down.
    summaryOnly: isMiniReportEnabled,
  })

  const handleTraitHover = (id) => {
    if (id) {
      const refId = setTimeout(() => setActiveTrait(id), 400);
      hoverDelayRef.current = refId;
    } else {
      clearTimeout(hoverDelayRef.current);
      setActiveTrait(null);
    }
  };

  const activeBucketData = activeBucketId
    ? [
      ...functionalBuckets?.buckets,
      ...communicationBuckets?.buckets,
      ...competenciesBuckets?.buckets,
      ...aptitudeBuckets?.buckets,
      ...otherBuckets
    ].find(
      ({ id }) => id === activeBucketId
    )
    : null;

  const hideFitScore = useMemo(() => {
    if (!positionConfig?.positionId) return true;

    const { positionId } = positionConfig;
    const hiddenFitScore = TEMP_NO_FITSCORE_POSITIONS.includes(positionId);

    return hiddenFitScore || checkOnlyPersonalityBucket;
  }, [positionConfig, reportData?.buckets]);

  console.log(reportData?.fitScore)
  return (
    <div id="one-page-report-component" className="v2">
      <div className="info v2">
        <ReportHeader
          hideCta
          reportData={reportData}
          linkConfig={linkConfig}
          positionConfig={positionConfig}
          userVideoSessionsList={userVideoSessionsList}
          isNonDnaReport={isNonDnaReport}
          logMixpanelEvent={logMixpanelEvent}
          showProctoringModal={() => setIsProctoringModalVisible(true)}
          criticalSnapshotsCount={positionConfig?.aiProctoring ? criticalSnapshots?.length : reportData?.proctoredImages?.critical}
          totalSnapshotsCount={positionConfig?.aiProctoring ? allSnapshots?.length : reportData?.proctoredImages?.total}
          fetchSnapshotsForReportDownload={fetchSnapshotsForReportDownload}
          referenceImageLink={referenceImageLink}
          hideFitScore={hideFitScore}
          companyId={reportData?.position?.organizationId}
          hideVerdictPills={reportData?.cutOffCleared != null}
          candidateFormData={candidateFormData}
        />
        <div className="content-wrapper">
          <div className="max-width-left">
            <div className="report-candidate-section">
              <CandidateDetailsSection
                formData={candidateFormData}
                loading={candidateFormDataLoading}
                hideWhenEmpty
                reportView
                collapsible
                defaultCollapsed
              />
            </div>
            {!activeBucketId ? (
              <div>
                {
                  reportData?.cutOffCleared != null ? (
                    <>
                      <OverallSummary reportData={reportData} positionConfig={positionConfig} />
                      <CutoffTraitsTable reportData={reportData} />
                      {reportData?.fitScore && reportData?.fitScore?.cutOff == null && (
                        <div className="group-container">
                          <TraitsGrid
                            showBucketWise
                            hideLegends
                            showScore
                            disableInteraction
                            title="Overall Score"
                            description="Overall score based on performance across all assessed skills."
                            traitsMap={getTraitsMap([{
                              traitsMeta: {
                                fitScore: {
                                  id: "fitScore",
                                  displayName: "Overall Score",
                                  upperBound: { score: reportData.fitScore.data?.upperBound },
                                  lowerBound: { score: reportData.fitScore.data?.lowerBound },
                                  score: reportData.fitScore.score,
                                  median: reportData.fitScore.data?.median,
                                }
                              }
                            }])}
                            activeTrait={activeTrait}
                            onTraitHover={handleTraitHover}
                          />
                        </div>
                      )}
                    </>
                  ) : hideFitScore ? null : (
                    <FitScore
                      {...reportData?.fitScore}
                      dnaScore={null}
                      showLegends={isNonDnaReport}
                      fitScoreMultiplier={positionConfig?.fitScoreMultiplier ?? 1}
                      hideIndustryAverage={personalityBuckets.enableBucketAnalysis?.personalityBucket && personalityBuckets.bucketAnalysis?.personalityBucket}
                    />
                  )
                }
                <BehaviouralAnalysis behaviouralAnalysis={reportData?.behaviouralAnalysis} />
                {!!functionalBuckets.buckets?.length > 0 && (
                  <div className="single-bucket">
                    <div>
                      <h4 className="single-bucket--name">
                        {functionalBuckets.name}
                      </h4>
                      <p className="single-bucket--description">
                        {functionalBuckets.description}
                      </p>
                    </div>
                    <div>
                      {functionalBuckets.buckets.map((bucket) =>
                        renderSpecialBuckets(bucket)
                      )}
                    </div>
                  </div>
                )}
                {!!communicationBuckets.buckets?.length > 0 && (
                  <div className="single-bucket">
                    <div>
                      <h4 className="single-bucket--name">
                        {communicationBuckets.name}
                      </h4>
                      <p className="single-bucket--description">
                        {communicationBuckets.description}
                      </p>
                    </div>
                    <div>
                      {communicationBuckets.buckets.map((bucket) =>
                        renderSpecialBuckets(bucket)
                      )}
                    </div>
                  </div>
                )}
                {!!competenciesBuckets.buckets?.length > 0 && (
                  <div className="single-bucket">
                    <div>
                      <h4 className="single-bucket--name">
                        {competenciesBuckets.name}
                      </h4>
                      <p className="single-bucket--description">
                        {competenciesBuckets.description}
                      </p>
                    </div>
                    <div>
                      {competenciesBuckets.buckets.map((bucket) =>
                        renderSpecialBuckets(bucket)
                      )}
                    </div>
                  </div>
                )}
                {!!aptitudeBuckets.buckets?.length > 0 && (
                  <div className="single-bucket">
                    <div>
                      <h4 className="single-bucket--name">
                        {aptitudeBuckets.name}
                      </h4>
                      <p className="single-bucket--description">
                        {aptitudeBuckets.description}
                      </p>
                    </div>
                    <div>
                      {aptitudeBuckets.buckets.map((bucket) =>
                        renderSpecialBuckets(bucket)
                      )}
                    </div>
                  </div>
                )}
                {!!otherBuckets?.length > 0 && (
                  otherBuckets.map((bucket) => (
                    <div key={bucket.id} className="single-bucket">
                      <div>
                        <h4 className="single-bucket--name">
                          {bucket.name}
                        </h4>
                        {bucket.description && (
                          <p className="single-bucket--description">
                            {bucket.description}
                          </p>
                        )}
                      </div>
                      <div>
                        {renderSpecialBuckets(bucket)}
                      </div>
                    </div>
                  ))
                )}
                {!!reportData &&
                  otherBucketsWithGridView?.map(
                    ({ id, name, description, traitsMeta }) =>
                      !Object.values(BUCKET_NAMES).includes(name) ? (
                        <div key={id} className="group-container">
                          <TraitsGrid
                            showBucketWise
                            hideLegends
                            title={name}
                            description={description}
                            traitsMap={getTraitsMap([{ traitsMeta }])}
                            activeTrait={activeTrait}
                            onTraitHover={handleTraitHover}
                          />
                        </div>
                      ) : null
                  )}
                {!!personalityBuckets.traits?.length > 0 &&
                  <PersonalityComponent
                    bucket={personalityBuckets}
                  />
                }
                {!!personalityBucketsIPIPLong.traits?.length > 0 &&
                  <PersonalityComponent
                    bucket={personalityBucketsIPIPLong}
                  />
                }
                {!!personalityBucketsIPIPShort.traits?.length > 0 &&
                  <PersonalityComponent
                    bucket={personalityBucketsIPIPShort}
                  />
                }
              </div>
            ) : (
              activeBucketData && (
                <BucketDetails
                  {...activeBucketData}
                  reportData={reportData}
                  companyId={reportData?.position?.organizationId}
                  headerTraitColor={"grey"}
                  onBack={() => setActiveBucketId(null)}
                  audioProctoring={positionConfig?.audioProctoring}
                  plagiarismConfig={{
                    plagiarism: positionConfig?.plagiarism,
                    maxAllowed: positionConfig?.maxAllowedPlagiarism,
                    minAllowed: positionConfig?.minAllowedPlagiarism
                  }}
                />
              )
            )}
          </div>
        </div>
      </div>
      <ProctoringModal
        isProctoringModalVisible={isProctoringModalVisible}
        setIsProctoringModalVisible={setIsProctoringModalVisible}
        linkConfig={linkConfig}
        positionConfig={positionConfig}
        userVideoSessionsList={userVideoSessionsList}
        setUserVideoSessionsList={setUserVideoSessionsList}
        allSnapshots={allSnapshots}
        setAllSnapshots={setAllSnapshots}
        criticalSnapshots={criticalSnapshots}
        setCriticalSnapshots={setCriticalSnapshots}
        tabSwitched={reportData?.tabSwitched ?? 0}
        trustScore={reportData?.trustScore}
        proctoringResultUpdatedAt={reportData?.proctoringResultUpdatedAt}
        cutOffCleared={reportData?.cutOffCleared}
        fetchSessionSnapshots={fetchSessionSnapshots}
        referenceImageLink={referenceImageLink}
        audioProctoringReport={reportData?.audioProctoringReport}
      />
    </div>
  );
};
